import { Component, ElementRef, HostListener, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { MasterService } from 'src/app/core/services/master.service';
import { AppsModel } from 'src/app/features/admin-portal/modules/subscription/models/subscription.models';
import { SubscriptionService } from 'src/app/features/admin-portal/modules/subscription/services/subscription.service';
import { MobileAppVersionModel } from 'src/app/features/admin-portal/modules/upload-build/models/upload-build.model';
import { UploadBuildService } from 'src/app/features/admin-portal/modules/upload-build/services/upload-build.service';
import { UserModel } from 'src/app/features/company-portal/modules/user-management/models/user-management.models';
import { UsersManagementService } from 'src/app/features/company-portal/modules/user-management/services/user-management.service';
import { environment } from '../../../environments/environment';
import { ISBETAVERSION, ISLATESTBUILD } from '../../constants/constants';
import { AppPlatform, Apps, MobileApp } from '../../enums/enums';
interface AppVersionInfo {
  version: string;
  lastUpdated: string;
  url: string;
}

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.css'],
  standalone: false,
})
export class HomeComponent {
  @ViewChild('defaultStoreWarning')
  defaultStoreWarning?: ElementRef<HTMLElement>;
  imsUrl: string = environment.imsUrl;
  userInfo: any;
  allApps: Array<AppsModel> = [];
  userApps: Array<AppsModel> = [];
  userObj: UserModel = new UserModel();
  tpmUrl: string = environment.tpmUrl;
  listerUrl: string = environment.listerUrl;
  posUrl: string = environment.posUrl;
  contextMenuPosition = { x: '0px', y: '0px' };
  showContextMenu = false;
  selectedApp: any = null;
  isNotSufficientPermission: boolean = false;
  canShowTpmWindowAppDownloadOption: boolean = environment.canShowTpmWindowAppDownloadOption;
  canShowTpmAndroidAppDownloadOption: boolean = environment.canShowTpmAndroidAppDownloadOption;
  canShowPosWindowAppDownloadOption: boolean = environment.canShowPosWindowAppDownloadOption;
  canShowPosAndroidAppDownloadOption: boolean = environment.canShowPosAndroidAppDownloadOption;
  canShowImsOption: boolean = environment.canShowImsOption;
  mobileAppVersions: MobileAppVersionModel[] = [];
  appVersions: { [key: string]: AppVersionInfo } = {};
  constructor(
    private userManagementService: UsersManagementService,
    private masterService: MasterService,
    private subscriptionService: SubscriptionService,
    private router: Router,
    private mobileAppVersionService: UploadBuildService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.userInfo = this.masterService.getUserDetailFromStorage();
    await this.getAllAppsList();
    this.getUserDetails();
    this.getMobileAppVersions();
  }

  async getAllAppsList(): Promise<void> {
    try {
      let res = await firstValueFrom(this.subscriptionService.getAllApps());
      let apps = res as AppsModel[];
      this.allApps = this.subscriptionService.formatAppListing(apps);
    } catch (error) {
      console.error('Error fetching apps:', error);
    }
  }

  getUserDetails(isAfterUpdateCall: boolean = false): Promise<void> {
    const userId = this.userInfo.userid;
    return new Promise((resolve, reject) => {
      this.userManagementService.getUserLiteById(userId, isAfterUpdateCall).subscribe({
        next: (res) => {
          this.userObj = res as UserModel;
          const userAppsIds = this.userObj.apps.map((item) => item.id);
          this.userApps = this.allApps.filter((item) => userAppsIds.includes(item.id));

          resolve();
          this.isNotSufficientPermission = this.userApps.length == 0;
        },
        error: (err) => reject(err),
      });
    });
  }
  getMobileAppVersions() {
    this.mobileAppVersionService
      .getBuilds(environment.id, ISLATESTBUILD, ISBETAVERSION)
      .subscribe((result) => {
        this.mobileAppVersions = result as MobileAppVersionModel[];

        const combos = [
          // TPM Beta
          {
            key: 'tpm-beta-android',
            app: MobileApp.TpmOffline,
            platform: AppPlatform.Android,
            beta: true,
          },
          {
            key: 'tpm-beta-windows',
            app: MobileApp.TpmOffline,
            platform: AppPlatform.Windows,
            beta: true,
          },

          // TPM Latest
          {
            key: 'tpm-android',
            app: MobileApp.TpmOffline,
            platform: AppPlatform.Android,
            beta: false,
          },
          {
            key: 'tpm-windows',
            app: MobileApp.TpmOffline,
            platform: AppPlatform.Windows,
            beta: false,
          },

          // POS Beta
          {
            key: 'pos-beta-android',
            app: MobileApp.PosOnline,
            platform: AppPlatform.Android,
            beta: true,
          },
          {
            key: 'pos-beta-windows',
            app: MobileApp.PosOnline,
            platform: AppPlatform.Windows,
            beta: true,
          },

          // POS Latest
          {
            key: 'pos-android',
            app: MobileApp.PosOnline,
            platform: AppPlatform.Android,
            beta: false,
          },
          {
            key: 'pos-windows',
            app: MobileApp.PosOnline,
            platform: AppPlatform.Windows,
            beta: false,
          },
        ];

        combos.forEach(({ key, app, platform, beta }) => {
          const match = this.mobileAppVersions.find(
            (x) =>
              x.mobileApp === app &&
              x.platform === platform &&
              (beta ? x.isBetaVersion : x.isLatest),
          );

          if (match) {
            this.appVersions[key] = {
              version: match.version,
              lastUpdated: this.formatDate(match.lastUpdatedAt),
              url: match.downloadUrl,
            };
          }
        });
      });
  }

  formatDate(dateStr: string): string {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }
  redirectToModule(appId: number) {
    switch (appId) {
      case Apps.TPMPortal:
        if (this.userObj.defaultStoreId) {
          const baseUrl = this.tpmUrl.split('?')[0];
          this.tpmUrl = `${baseUrl}?appId=${appId}`;
          window.location.href = this.tpmUrl;
        } else {
          this.defaultStoreWarning?.nativeElement.click();
        }
        break;

      case Apps.POS:
        window.location.href = this.posUrl;
        break;

      case Apps.Lister:
        window.location.href = this.listerUrl;
        break;

      default:
        console.error('Invalid module name');
        return;
    }
  }

  onRightClick(event: MouseEvent, item: any) {
    event.preventDefault();
    this.selectedApp = item;

    this.contextMenuPosition.x = `${event.clientX}px`;
    this.contextMenuPosition.y = `${event.clientY}px`;
    this.showContextMenu = true;
  }

  openInNewTab() {
    this.showContextMenu = false;
    if (this.selectedApp) {
      const appId = this.selectedApp.id;
      const url = this.getAppUrl(appId);
      if (appId == 1 && !this.userObj.defaultStoreId) {
        this.defaultStoreWarning?.nativeElement.click();
      } else {
        window.open(url, '_blank');
      }
    }
  }

  // Create a method to build the correct URL
  getAppUrl(appId: number): string {
    switch (appId) {
      case Apps.TPMPortal:
        const baseUrl = this.tpmUrl.split('?')[0];
        return `${baseUrl}?appId=${appId}`;
      case Apps.POS:
        return this.posUrl;
      case Apps.Lister:
        return this.listerUrl;
      default:
        return '';
    }
  }

  nevigateToDefaultStoreConfig() {
    this.router.navigate(['/company-portal/user-management/user']);
  }

  // Optional: Close context menu on outside click
  @HostListener('document:click')
  onDocumentClick() {
    this.showContextMenu = false;
  }
  accessIMS() {
    window.location.href = this.imsUrl;
  }
  accessListerMobile(appId: number) {
    if (appId == 4) {
      window.location.href = `${this.listerUrl}/product-list-mobile`;
    }
  }
}
